import React, { useState } from 'react';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Alert, Button, Snackbar, Stack, TextField, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAuthForm } from '@/contexts';
import { useLogin } from '@/features/account';
import { ORDERS_ROUTE } from '@/routes';
import palette from '@/theme/palette.ts';

import { styles } from './styles.ts';

export const AuthSide = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { authValues, setAuthValues } = useAuthForm();

  const [snackBarState, setSnackBarState] = useState<boolean>(false);

  const handleCloseSnackBar = () => {
    setTimeout(() => {
      setSnackBarState(false);
    }, 4000);
  };

  const onLoginSuccess = () => {
    navigate(ORDERS_ROUTE);
  };

  const onLoginError = () => {
    setSnackBarState(true);
    setError('email', { message: '' });
    setError('password', { message: '' });
    handleCloseSnackBar();
  };

  const { login } = useLogin({ onSuccess: onLoginSuccess, onError: onLoginError });

  const {
    handleSubmit,
    register,
    setError,
    formState: { errors },
  } = useForm({
    defaultValues: {
      email: authValues.email,
      password: authValues.password,
    },
    mode: 'onSubmit',
  });

  const onSubmit = async (data: { email: string; password: string }) => {
    setAuthValues(data);
    const { email, password } = data;
    await login(email, password);
  };

  return (
    <Stack direction="row" justifyContent="center" alignItems="center" sx={styles.container}>
      <Stack direction="column" justifyContent="center" alignItems="center" sx={{ width: '100%' }}>
        <Typography
          variant="h1"
          sx={{
            fontWeight: '700',
            marginBottom: '20px',
            color: palette.primary.main,
          }}
        >
          {t('AUTH.LOGIN')}
        </Typography>
        <Snackbar anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }} open={snackBarState}>
          <Alert
            severity="error"
            icon={<ErrorOutlineIcon fontSize="inherit" />}
            variant="filled"
            sx={{ background: palette.toast.error }}
          >
            Nesprávné přihlašovací údaje
          </Alert>
        </Snackbar>
        <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%' }}>
          <Stack
            direction="column"
            justifyContent="center"
            alignItems="center"
            sx={{ width: '100%' }}
          >
            <TextField
              sx={{
                ...styles.textField,
                '& .MuiInputBase-input': {
                  backgroundColor: palette.background.default,
                },
                '& .MuiFormLabel-root': {
                  color: palette.text.info,
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '0.875rem',
                },
              }}
              {...register('email', { required: t('REQUIRES.EMAIL') })}
              label={t('FORMS.EMAIL')}
              variant="outlined"
              margin="normal"
              error={!!errors.email}
              helperText={errors.email?.message}
            />

            <TextField
              sx={{
                ...styles.textField,
                '& .MuiInputBase-input': {
                  backgroundColor: palette.background.default,
                },
                '& .MuiFormLabel-root': {
                  color: palette.text.info,
                },
                '& .MuiFormHelperText-root': {
                  fontSize: '0.875rem',
                },
              }}
              {...register('password', { required: t('REQUIRES.PASSWORD') })}
              label={t('FORMS.PASSWORD')}
              type="password"
              variant="outlined"
              margin="normal"
              error={!!errors.password}
              helperText={errors.password?.message}
            />
            <Button
              type="submit"
              sx={{
                ...styles.submitButton,
                backgroundColor: palette.primary.clicked,
                color: palette.text.contrast,
                '&:hover': {
                  backgroundColor: palette.primary.main,
                },
              }}
            >
              {t('AUTH.TO_LOGIN')}
            </Button>
          </Stack>
        </form>
      </Stack>
    </Stack>
  );
};
